/**
 * Nutri-Leaf long-form "On this page" table of contents (RV-35).
 *
 * Builds a TOC from H2/H3 headings in the section body: assigns stable
 * anchor ids (slugified, deduplicated), fills the list, reveals the TOC
 * only when at least one heading exists, tracks the active heading with an
 * IntersectionObserver, and scrolls smoothly on link click (hard jump when
 * prefers-reduced-motion). The mobile <details> collapse is opened on
 * desktop widths via matchMedia.
 *
 * Progressive enhancement: with JS disabled the TOC stays hidden (nothing
 * to fill it) and the body renders unchanged.
 */
(function () {
  const root = document.querySelector('[data-nl-long-form]');
  if (!root) return;

  const tocWrap = root.querySelector('[data-nl-toc]');
  const tocList = root.querySelector('[data-nl-toc-list]');
  const bodyRoot = root.querySelector('[data-nl-toc-root]');
  if (!tocWrap || !tocList || !bodyRoot) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Slugify like Shopify handles: lowercase, spaces to dashes, strip symbols.
  function slugify(text) {
    return text
      .toLowerCase()
      .trim()
      .replace(/[\s]+/g, '-')
      .replace(/[^\wÀ-ɏ-]/g, '')
      .replace(/-+/g, '-');
  }

  const seen = new Map();
  function uniqueId(base) {
    const count = seen.get(base) || 0;
    seen.set(base, count + 1);
    return count === 0 ? base : `${base}-${count}`;
  }

  // Build the list from body headings. Only H2/H3 — H1 ownership is the hero.
  const headings = bodyRoot.querySelectorAll('h2, h3');
  const links = [];
  headings.forEach((heading, index) => {
    if (!heading.id) {
      heading.id = uniqueId(slugify(heading.textContent) || `section-${index + 1}`);
    }
    const item = document.createElement('li');
    const link = document.createElement('a');
    link.href = `#${heading.id}`;
    link.textContent = heading.textContent;
    link.className = heading.tagName === 'H3' ? 'nl-long-form__toc-link--sub' : '';
    item.appendChild(link);
    tocList.appendChild(item);
    links.push({ link, heading });
  });

  if (links.length === 0) {
    tocWrap.remove();
    return;
  }

  // Reveal only now that the list has content — one atomic layout change.
  tocWrap.removeAttribute('data-nl-toc-hidden');

  // Desktop (>= 750px) opens the <details>; mobile keeps native collapse.
  const details = tocWrap.querySelector('details');
  const desktopMq = window.matchMedia('(min-width: 750px)');
  function syncDetails() {
    if (details) details.open = desktopMq.matches;
  }
  syncDetails();
  if (typeof desktopMq.addEventListener === 'function') {
    desktopMq.addEventListener('change', syncDetails);
  } else {
    desktopMq.addListener(syncDetails); // Safari 13 fallback
  }

  // Smooth scroll, honouring reduced motion.
  links.forEach(({ link }) => {
    link.addEventListener('click', (event) => {
      event.preventDefault();
      const target = document.getElementById(link.getAttribute('href').slice(1));
      if (!target) return;
      target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      history.replaceState(null, '', link.getAttribute('href'));
    });
  });

  // Active-heading tracking: highlight the TOC entry of the heading
  // currently at the top of the viewport (below the sticky header).
  const tocLinks = Array.from(tocList.querySelectorAll('a'));
  const headerOffset = 120; // sticky header + breathing room
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const id = entry.target.id;
        tocLinks.forEach((a) => {
          const active = a.getAttribute('href') === `#${id}`;
          a.classList.toggle('is-active', active);
          a.setAttribute('aria-current', active ? 'location' : 'false');
        });
      });
    },
    { rootMargin: `-${headerOffset}px 0px -75% 0px`, threshold: 0 }
  );
  headings.forEach((heading) => observer.observe(heading));
})();
